/**
 * Client-side image resize/compress before upload.
 *
 * Modern phones shoot 12-24 megapixel photos at 4-12 MB each. Full-quality
 * originals are overkill for insurance/damage evidence and slow to upload
 * over 4G/rural cell. This helper decodes the image on-device, resamples
 * it down to a max 1600px longest edge (keeps aspect ratio), and re-encodes
 * as JPEG at quality 0.85. Typical result: ~300-600 KB, visually
 * indistinguishable from the original for panel-damage documentation.
 *
 * If the resize path fails for any reason (very small screens, exotic
 * formats, out-of-memory on a low-end phone), we fall back to the
 * ORIGINAL file. Never block the upload.
 *
 * EXIF orientation: The `createImageBitmap({ imageOrientation: "from-image" })`
 * option applies the EXIF rotation flag automatically so portrait photos
 * from iPhones don't come out sideways.
 */

const MAX_EDGE_PX = 1600;
const JPEG_QUALITY = 0.85;

// Only resize actual raster images. HEIC/HEIF is included deliberately: an
// iPhone set to "High Efficiency" can hand over a 4-12 MB HEIC that Chrome
// then can't display at all. Safari decodes it with the system codec, so the
// attempt usually succeeds and converts it to JPEG; where it doesn't, the
// catch below sends the original exactly as before (Oct 2026).
const RESIZABLE_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
]);


export async function resizeImageForUpload(file) {
  try {
    if (!(file instanceof Blob)) return file;
    if (!RESIZABLE_TYPES.has((file.type || "").toLowerCase())) return file;
    // Skip resize if the original is already reasonably small (< 500 KB)
    // — no point paying the decode cost.
    if (file.size < 500 * 1024) return file;

    // Decode. `imageOrientation: "from-image"` honours the EXIF rotate flag
    // so we don't upload sideways portraits.
    let bitmap;
    try {
      bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      // Some browsers (older Safari) don't accept the options bag. Retry
      // plain — orientation may be wrong for that user, but upload still works.
      bitmap = await createImageBitmap(file);
    }

    const { width, height } = bitmap;
    const longestEdge = Math.max(width, height);
    // Already small enough — no re-encode needed. Close the bitmap and
    // return the original so we don't lose any quality unnecessarily.
    if (longestEdge <= MAX_EDGE_PX) {
      bitmap.close?.();
      return file;
    }

    const scale = MAX_EDGE_PX / longestEdge;
    const targetW = Math.round(width * scale);
    const targetH = Math.round(height * scale);

    // OffscreenCanvas is fastest but not in every browser — fall back to
    // a regular <canvas>.
    let canvas;
    if (typeof OffscreenCanvas !== "undefined") {
      canvas = new OffscreenCanvas(targetW, targetH);
    } else {
      canvas = document.createElement("canvas");
      canvas.width = targetW;
      canvas.height = targetH;
    }
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);
    bitmap.close?.();

    // Encode. Use JPEG regardless of source — reliably decodable everywhere,
    // small file sizes, insurers accept them.
    let blob;
    if (canvas.convertToBlob) {
      blob = await canvas.convertToBlob({ type: "image/jpeg", quality: JPEG_QUALITY });
    } else {
      blob = await new Promise((resolve) => {
        canvas.toBlob((b) => resolve(b), "image/jpeg", JPEG_QUALITY);
      });
    }
    if (!blob) return file; // paranoid fallback

    // Preserve the original name (swap extension to .jpg since we just re-encoded).
    const baseName = (file.name || "photo").replace(/\.[^.]+$/, "");
    return new File([blob], `${baseName}.jpg`, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch {
    // Any decode/encode failure — send the original. Upload never blocked.
    return file;
  }
}
